import { Attr, Cell, Color, EMPTY_CELL, Rect, TRANSPARENT } from './types';

/**
 * A dense rectangular buffer of terminal cells.
 *
 * Stored as a struct-of-arrays so that a 300x150 document is three small typed
 * arrays plus one string array rather than 45 000 heap objects. All mutating
 * methods are clipped to the grid bounds, so tools never need to bounds-check.
 */
export class Grid {
  readonly width: number;
  readonly height: number;

  /** Grapheme cluster per cell; '' means unpainted. */
  private readonly chars: string[];
  private readonly fg: Int32Array;
  private readonly bg: Int32Array;
  private readonly attrs: Uint16Array;

  constructor(width: number, height: number) {
    if (width <= 0 || height <= 0) throw new RangeError(`invalid grid size ${width}x${height}`);
    this.width = width;
    this.height = height;
    const n = width * height;
    this.chars = new Array<string>(n).fill('');
    this.fg = new Int32Array(n).fill(TRANSPARENT);
    this.bg = new Int32Array(n).fill(TRANSPARENT);
    this.attrs = new Uint16Array(n);
  }

  get length(): number {
    return this.width * this.height;
  }

  index(x: number, y: number): number {
    return y * this.width + x;
  }

  inBounds(x: number, y: number): boolean {
    return x >= 0 && y >= 0 && x < this.width && y < this.height;
  }

  get(x: number, y: number): Cell {
    if (!this.inBounds(x, y)) return EMPTY_CELL;
    const i = y * this.width + x;
    return { char: this.chars[i], fg: this.fg[i], bg: this.bg[i], attrs: this.attrs[i] };
  }

  getChar(x: number, y: number): string {
    return this.inBounds(x, y) ? this.chars[y * this.width + x] : '';
  }

  isEmpty(x: number, y: number): boolean {
    if (!this.inBounds(x, y)) return true;
    const i = y * this.width + x;
    return this.chars[i] === '' && this.bg[i] === TRANSPARENT && this.fg[i] === TRANSPARENT;
  }

  /** Writes a cell. Returns true when the contents actually changed. */
  set(x: number, y: number, cell: Cell): boolean {
    if (!this.inBounds(x, y)) return false;
    const i = y * this.width + x;
    if (
      this.chars[i] === cell.char &&
      this.fg[i] === cell.fg &&
      this.bg[i] === cell.bg &&
      this.attrs[i] === cell.attrs
    ) {
      return false;
    }
    this.chars[i] = cell.char;
    this.fg[i] = cell.fg;
    this.bg[i] = cell.bg;
    this.attrs[i] = cell.attrs;
    return true;
  }

  setAt(index: number, cell: Cell): void {
    this.chars[index] = cell.char;
    this.fg[index] = cell.fg;
    this.bg[index] = cell.bg;
    this.attrs[index] = cell.attrs;
  }

  getAt(index: number): Cell {
    return {
      char: this.chars[index],
      fg: this.fg[index],
      bg: this.bg[index],
      attrs: this.attrs[index],
    };
  }

  clear(): void {
    this.chars.fill('');
    this.fg.fill(TRANSPARENT);
    this.bg.fill(TRANSPARENT);
    this.attrs.fill(Attr.NONE);
  }

  clearRect(r: Rect): void {
    this.fillRect(r, EMPTY_CELL);
  }

  fillRect(r: Rect, cell: Cell): void {
    const x0 = Math.max(0, r.x);
    const y0 = Math.max(0, r.y);
    const x1 = Math.min(this.width, r.x + r.width);
    const y1 = Math.min(this.height, r.y + r.height);
    for (let y = y0; y < y1; y++) {
      for (let x = x0; x < x1; x++) this.set(x, y, cell);
    }
  }

  clone(): Grid {
    const g = new Grid(this.width, this.height);
    for (let i = 0; i < this.chars.length; i++) g.chars[i] = this.chars[i];
    g.fg.set(this.fg);
    g.bg.set(this.bg);
    g.attrs.set(this.attrs);
    return g;
  }

  /** Copies this grid into a new one of a different size, anchored top-left. */
  resized(width: number, height: number, offsetX = 0, offsetY = 0): Grid {
    const g = new Grid(width, height);
    for (let y = 0; y < this.height; y++) {
      const ty = y + offsetY;
      if (ty < 0 || ty >= height) continue;
      for (let x = 0; x < this.width; x++) {
        const tx = x + offsetX;
        if (tx < 0 || tx >= width) continue;
        g.setAt(ty * width + tx, this.get(x, y));
      }
    }
    return g;
  }

  /** Extracts a sub-region as a standalone grid. */
  extract(r: Rect): Grid {
    const g = new Grid(Math.max(1, r.width), Math.max(1, r.height));
    for (let y = 0; y < r.height; y++) {
      for (let x = 0; x < r.width; x++) {
        g.set(x, y, this.get(r.x + x, r.y + y));
      }
    }
    return g;
  }

  /** Draws `src` at (dx, dy). Empty source cells are skipped unless `opaque`. */
  blit(src: Grid, dx: number, dy: number, opaque = false): void {
    for (let y = 0; y < src.height; y++) {
      for (let x = 0; x < src.width; x++) {
        const c = src.get(x, y);
        if (!opaque && c.char === '' && c.bg === TRANSPARENT && c.fg === TRANSPARENT) continue;
        this.set(dx + x, dy + y, c);
      }
    }
  }

  /**
   * Composites `src` over this grid, honouring per-channel transparency:
   * an empty source char keeps the char below, a transparent bg keeps the
   * bg below. This is how layers are flattened.
   */
  composite(src: Grid, offsetX = 0, offsetY = 0): void {
    for (let y = 0; y < src.height; y++) {
      const ty = y + offsetY;
      if (ty < 0 || ty >= this.height) continue;
      for (let x = 0; x < src.width; x++) {
        const tx = x + offsetX;
        if (tx < 0 || tx >= this.width) continue;
        const si = y * src.width + x;
        const ch = src.chars[si];
        const sbg = src.bg[si];
        if (ch === '' && sbg === TRANSPARENT) continue;
        const ti = ty * this.width + tx;
        if (ch !== '') {
          this.chars[ti] = ch;
          this.fg[ti] = src.fg[si];
          this.attrs[ti] = src.attrs[si];
        }
        if (sbg !== TRANSPARENT) this.bg[ti] = sbg;
      }
    }
  }

  /** Bounding box of all visually meaningful cells, or null when blank. */
  contentBounds(): Rect | null {
    let minX = this.width;
    let minY = this.height;
    let maxX = -1;
    let maxY = -1;
    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        const i = y * this.width + x;
        const blank =
          (this.chars[i] === '' || this.chars[i] === ' ') &&
          this.bg[i] === TRANSPARENT &&
          this.attrs[i] === 0;
        if (blank) continue;
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
    if (maxX < 0) return null;
    return { x: minX, y: minY, width: maxX - minX + 1, height: maxY - minY + 1 };
  }

  flipHorizontal(): Grid {
    const g = new Grid(this.width, this.height);
    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        g.set(this.width - 1 - x, y, mirrorCell(this.get(x, y), H_MIRROR));
      }
    }
    return g;
  }

  flipVertical(): Grid {
    const g = new Grid(this.width, this.height);
    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        g.set(x, this.height - 1 - y, mirrorCell(this.get(x, y), V_MIRROR));
      }
    }
    return g;
  }

  equals(other: Grid): boolean {
    if (other.width !== this.width || other.height !== this.height) return false;
    return this.diff(other).length === 0;
  }

  /** Indices whose contents differ from `other`. Used for differential render. */
  diff(other: Grid): number[] {
    const out: number[] = [];
    const n = Math.min(this.length, other.length);
    for (let i = 0; i < n; i++) {
      if (
        this.chars[i] !== other.chars[i] ||
        this.fg[i] !== other.fg[i] ||
        this.bg[i] !== other.bg[i] ||
        this.attrs[i] !== other.attrs[i]
      ) {
        out.push(i);
      }
    }
    return out;
  }

  forEachCell(fn: (cell: Cell, x: number, y: number) => void): void {
    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) fn(this.get(x, y), x, y);
    }
  }

  /** Plain text dump, trailing whitespace trimmed. Handy in tests. */
  toText(): string {
    const lines: string[] = [];
    for (let y = 0; y < this.height; y++) {
      let line = '';
      for (let x = 0; x < this.width; x++) {
        const c = this.chars[y * this.width + x];
        line += c === '' ? ' ' : c;
      }
      lines.push(line.replace(/\s+$/, ''));
    }
    return lines.join('\n');
  }

  static fromText(text: string, fg: Color = TRANSPARENT, bg: Color = TRANSPARENT): Grid {
    const lines = text.split('\n');
    const width = Math.max(1, ...lines.map((l) => [...l].length));
    const g = new Grid(width, Math.max(1, lines.length));
    lines.forEach((line, y) => {
      [...line].forEach((ch, x) => {
        g.set(x, y, { char: ch, fg, bg, attrs: Attr.NONE });
      });
    });
    return g;
  }
}

/** Characters whose meaning flips when mirrored left/right. */
const H_MIRROR: Record<string, string> = {
  '┌': '┐', '┐': '┌', '└': '┘', '┘': '└',
  '├': '┤', '┤': '├',
  '╔': '╗', '╗': '╔', '╚': '╝', '╝': '╚',
  '╠': '╣', '╣': '╠',
  '┏': '┓', '┓': '┏', '┗': '┛', '┛': '┗',
  '┣': '┫', '┫': '┣',
  '╭': '╮', '╮': '╭', '╰': '╯', '╯': '╰',
  '▌': '▐', '▐': '▌',
  '▖': '▗', '▗': '▖', '▘': '▝', '▝': '▘',
  '/': '\\', '\\': '/', '<': '>', '>': '<',
  '(': ')', ')': '(', '[': ']', ']': '[', '{': '}', '}': '{',
};

/** Characters whose meaning flips when mirrored top/bottom. */
const V_MIRROR: Record<string, string> = {
  '┌': '└', '└': '┌', '┐': '┘', '┘': '┐',
  '┬': '┴', '┴': '┬',
  '╔': '╚', '╚': '╔', '╗': '╝', '╝': '╗',
  '╦': '╩', '╩': '╦',
  '┏': '┗', '┗': '┏', '┓': '┛', '┛': '┓',
  '┳': '┻', '┻': '┳',
  '╭': '╰', '╰': '╭', '╮': '╯', '╯': '╮',
  '▀': '▄', '▄': '▀',
  '▘': '▖', '▖': '▘', '▝': '▗', '▗': '▝',
  '/': '\\', '\\': '/',
};

function mirrorCell(c: Cell, table: Record<string, string>): Cell {
  const m = table[c.char];
  return m ? { ...c, char: m } : c;
}
